import React, { useState, useEffect } from 'react';
import { Star, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { trackReviewSubmission } from '../utils/analytics';

const API = '/api';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 5,
    review_text: '',
    engineer_name: ''
  });

  // Fetch approved reviews on mount
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews?approved_only=true`);
        setReviews(response.data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    };
    fetchReviews();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.review_text) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/reviews`, formData);
      
      // Track review submission
      trackReviewSubmission(formData.rating);
      
      toast.success('Thank you! Your review has been submitted and will appear after approval.');
      setFormData({ name: '', email: '', rating: 5, review_text: '', engineer_name: '' });
    } catch (error) {
      console.error('Review submission error:', error);
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, onRate = null) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && onRate && onRate(star)}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          >
            <Star
              size={interactive ? 24 : 20}
              className={star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <section className="py-20 bg-gradient-to-b from-black via-purple-950/20 to-black">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Customer <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Reviews</span>
          </h2>
          <p className="text-gray-400 text-lg">
            See what our clients say about their experience
          </p>
        </div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12">
          {/* Reviews Display */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-white mb-6">Recent Reviews</h3>
            
            {reviews.length > 0 ? (
              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-white font-semibold">{review.name}</h4>
                        {review.engineer_name && (
                          <p className="text-sm text-purple-300">Session with {review.engineer_name}</p>
                        )}
                      </div>
                      {renderStars(review.rating)}
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed">{review.comment || review.review_text}</p>
                    <p className="text-xs text-gray-500 mt-3">
                      {new Date(review.created_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-12 text-center">
                <Star className="text-purple-400 mx-auto mb-4" size={48} />
                <p className="text-gray-400">Be the first to leave a review!</p>
              </div>
            )}
          </div>

          {/* Review Submission Form */}
          <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 backdrop-blur-sm border border-purple-500/20 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-white mb-6">Leave a Review</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Engineer (Optional)
                </label>
                <select
                  value={formData.engineer_name}
                  onChange={(e) => setFormData({ ...formData, engineer_name: e.target.value })}
                  className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                >
                  <option value="">Select engineer</option>
                  <option value="Trap Spade">Trap Spade</option>
                  <option value="Chapo">Chapo</option>
                  <option value="Dev">Dev</option>
                  <option value="Shotz">Shotz</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Rating *
                </label>
                {renderStars(formData.rating, true, (rating) => setFormData({ ...formData, rating }))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Your Review *
                </label>
                <textarea
                  required
                  value={formData.review_text}
                  onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                  rows={5}
                  className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 resize-none"
                  placeholder="Tell us about your experience..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    <span>Submit Review</span>
                  </>
                )}
              </button>

              <p className="text-xs text-gray-400 text-center">
                Your review will be visible after approval by our team
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
